// src/pages/OrdersPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
// Import onSnapshot, collection, query, where, orderBy from firebase/firestore
import { getRestaurantDetails, updateOrderStatus, updateOrderItems, updateOrderDetails, getMenuItems, getRestaurantWorkflow, moveOrderToNextStep } from '../services/firebaseService'; // Added workflow functions
import { db } from '../firebase'; // Import db
import { collection, query, orderBy, onSnapshot, Timestamp } from 'firebase/firestore'; // Import Firestore functions
import { getCurrencySymbol } from '../config/currencies'; // Import for currency symbol
import useOrderNotifications from '../hooks/useOrderNotifications'; // Import the custom hook
import LoadingSpinner from '../components/loadingspinner';
import { applyPriceAdjustment } from '../utils/priceHelper'; // Import price helper
import './OrdersPage.css'; // We'll create this CSS file later
import { FaPhone, FaWhatsapp } from 'react-icons/fa';

// Helper function to format time duration
const formatDuration = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (num) => String(num).padStart(2, '0');
  if (h > 0) {
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }
  return `${pad(m)}:${pad(s)}`;
};

// Timer component
function OrderTimer({ createdAt }) {
  const calculateTimePassed = useCallback(() => {
    if (!createdAt || !createdAt.toDate) return 0;
    const now = new Date();
    const createdDate = createdAt.toDate();
    return Math.max(0, Math.floor((now - createdDate) / 1000)); // in seconds
  }, [createdAt]);

  const [timePassed, setTimePassed] = useState(calculateTimePassed());

  useEffect(() => {
    setTimePassed(calculateTimePassed()); // Recalculate on createdAt change
    const intervalId = setInterval(() => {
      setTimePassed(prevTime => prevTime + 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [createdAt, calculateTimePassed]);

  if (!createdAt || !createdAt.toDate) {
    return <p><strong>Time Elapsed:</strong> N/A</p>;
  }

  return <p style={{ color: 'red' }}><strong>Time Elapsed:</strong> {formatDuration(timePassed)}</p>;
}

// Audio for notification is now handled by useOrderNotifications hook

function OrdersPage() {
  const { restaurantId } = useParams();
  useOrderNotifications(restaurantId); // Use the hook for notifications
  const [activeTab, setActiveTab] = useState('new'); // Changed to use workflow step IDs
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState({}); // Changed to object to store orders by status
  const [restaurant, setRestaurant] = useState(null); // State for restaurant details
  const [currencySymbol, setCurrencySymbol] = useState('$'); // Default currency symbol
  const [workflowSteps, setWorkflowSteps] = useState([]); // State for workflow steps
  
  // States for order editing
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [editedItems, setEditedItems] = useState([]);
  const [availableMenuItems, setAvailableMenuItems] = useState([]);
  const [selectedMenuItem, setSelectedMenuItem] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModalSeatAreaId, setSelectedModalSeatAreaId] = useState('');

  // Notification permission, sound, and display are now handled by useOrderNotifications hook

  // Ref to ignore initial seat category change on modal open
  const ignoreSeatCategoryChangeRef = React.useRef(false);

  useEffect(() => {
    const fetchRestaurantData = async () => {
      try {
        const details = await getRestaurantDetails(restaurantId);
        if (details) {
          setRestaurant(details);
          setCurrencySymbol(getCurrencySymbol(details.currency));
        } else {
          setError('Restaurant details not found.');
        }
        
        // Fetch workflow steps
        const workflow = await getRestaurantWorkflow(restaurantId);
        setWorkflowSteps(workflow);
        
        // Set initial active tab to first workflow step
        if (workflow.length > 0) {
          setActiveTab(workflow[0].id);
        }
      } catch (err) {
        console.error('Error fetching restaurant details:', err);
        setError(prevError => prevError || 'Failed to load restaurant details.');
      }
    };

    if (restaurantId) {
      fetchRestaurantData();
      setLoading(true);
      setError('');

      const ordersRef = collection(db, 'restaurants', restaurantId, 'orders');
      const q = query(ordersRef, orderBy('createdAt', 'desc'));

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        console.log("Snapshot received, processing orders...");
        const allOrders = [];
        // Notification logic is now handled by useOrderNotifications hook

        querySnapshot.forEach((doc) => {
          allOrders.push({ id: doc.id, ...doc.data() });
        });

        // Group orders by status
        const ordersByStatus = {};
        allOrders.forEach(order => {
          const status = order.status || 'new';
          if (!ordersByStatus[status]) {
            ordersByStatus[status] = [];
          }
          ordersByStatus[status].push(order);
        });
        
        setOrders(ordersByStatus);
        setLoading(false);

        // Update document title based on new orders count
        const firstStepId = workflowSteps.length > 0 ? workflowSteps[0].id : 'new';
        const newOrdersCount = ordersByStatus[firstStepId] ? ordersByStatus[firstStepId].length : 0;
        if (newOrdersCount > 0) {
            document.title = `(${newOrdersCount}) Orders`;
        } else {
            document.title = 'Orders';
        }

      }, (err) => {
        console.error('Error fetching orders with onSnapshot:', err);
        setError('Failed to load orders in real-time. Please try again.');
        setLoading(false);
      });

      // Cleanup listener on component unmount or when restaurantId changes
      return () => {
        console.log("Unsubscribing from orders listener for restaurant:", restaurantId);
        unsubscribe();
        document.title = 'CheckMenus'; // Reset title on unmount
      };
    } else {
      setLoading(false); // No restaurantId, so not loading
      setOrders({});
    }
  }, [restaurantId, currencySymbol]); // newOrders removed from dependencies as notification logic is externalized

  const handleConfirmOrder = async (orderId) => {
    try {
      await updateOrderStatus(restaurantId, orderId, 'completed');
    } catch (err) {
      console.error('Error confirming order:', err);
      setError('Failed to update order status. Please try again.');
    }
  };

  const handleCancelOrder = async (orderId) => {
    // Show confirmation dialog with optional reason input
    const userConfirmed = window.confirm('Are you sure you want to cancel this order?');
    
    if (userConfirmed) {
      const reason = window.prompt('Please enter the reason for cancellation (optional):');
      
      try {
        await updateOrderStatus(restaurantId, orderId, 'cancelled', reason || '');
      } catch (err) {
        console.error('Error cancelling order:', err);
        setError('Failed to cancel order. Please try again.');
      }
    }
  };
  
  // Function to open the edit modal for an order
  const handleEditOrder = async (order) => {
    try {
      setCurrentOrder(order);
      setEditedItems([...order.items]); // Create a copy of the order items
      
      // Fetch available menu items for the restaurant
      const menuItems = await getMenuItems(restaurantId);
      setAvailableMenuItems(menuItems); // Set this BEFORE setting seat category to ensure repricing can work if needed, though repricing is triggered by effect

      // Determine initial seat category ID
      let initialSeatAreaId = '';
      if (order.seatArea || order.seatCategory) {
          // Try to find by name, checking both new and old field names
          const areaName = order.seatArea || order.seatCategory;
          const cat = (restaurant.seatCategories || []).find(c => c.name === areaName);
          if (cat) initialSeatAreaId = cat.id;
      }
      
      // DO NOT fallback to default if not found. Preserve legacy price by initializing state but not repricing.
      
      setSelectedModalSeatAreaId(initialSeatAreaId);
      
      // Set flag to ignore the immediate useEffect trigger caused by setting the initial state
      ignoreSeatCategoryChangeRef.current = true;

      setIsEditModalOpen(true);
    } catch (err) {
      console.error('Error preparing to edit order:', err);
      setError('Failed to prepare order for editing. Please try again.');
    }
  };

  // Effect to reprice items when selectedModalSeatCategory changes
  useEffect(() => {
    if (!isEditModalOpen || !currentOrder) return;
    
    // Skip repricing if it's the initial load of the modal
    if (ignoreSeatCategoryChangeRef.current) {
        ignoreSeatCategoryChangeRef.current = false;
        return;
    }

    // Only reprice for table orders (online orders usually don't have seat categories)
    const orderType = currentOrder.phoneNumber && currentOrder.customerAddress ? 'online' : 'table';
    if (orderType === 'online') return;

    if (availableMenuItems.length === 0) return;
    
    // If no category is selected, do not reprice (preserve prices)
    if (!selectedModalSeatAreaId) return;

    const category = (restaurant.seatCategories || []).find(c => c.id === selectedModalSeatAreaId);
    
    setEditedItems(prevItems => prevItems.map(item => {
        // Find the original menu item to get the base price
        const menuItem = availableMenuItems.find(mi => mi.id === item.menuItemId);
        if (!menuItem) return item; // Keep existing price if menu item not found

        const basePrice = menuItem.offerPrice && menuItem.offerPrice < menuItem.price 
                            ? menuItem.offerPrice 
                            : menuItem.price;
                            
        // Recalculate price
        const newPrice = applyPriceAdjustment(basePrice, restaurant, "table", category);
        
        // Only update if price changed to avoid unnecessary re-renders loop if we put this in a dependency array 
        // (though map returns new array so setEditedItems will trigger)
        return { ...item, price: newPrice };
    }));

  }, [selectedModalSeatAreaId, availableMenuItems, isEditModalOpen, currentOrder, restaurant]);
  
  // Function to add a menu item to the order
  const handleAddMenuItem = () => {
    if (!selectedMenuItem || selectedQuantity < 1) return;
    
    const menuItem = availableMenuItems.find(item => item.id === selectedMenuItem);
    if (!menuItem) return;

    // Calculate price first to compare with existing items
    // Calculate price first to compare with existing items
    const orderType = currentOrder && currentOrder.phoneNumber && currentOrder.customerAddress ? 'online' : 'table';
    const basePrice = menuItem.offerPrice && menuItem.offerPrice < menuItem.price ? menuItem.offerPrice : menuItem.price;
    
    let adjustment = null;
    if (orderType === 'table' && selectedModalSeatAreaId) {
        adjustment = (restaurant.seatCategories || []).find(c => c.id === selectedModalSeatAreaId);
    }

    const adjustedPrice = applyPriceAdjustment(basePrice, restaurant, orderType, adjustment);
    
    // Check if the item already exists in the order with the SAME price
    const existingItemIndex = editedItems.findIndex(item => item.menuItemId === menuItem.id && item.price === adjustedPrice);
    
    if (existingItemIndex >= 0) {
      // Update quantity if item already exists with same price
      const updatedItems = [...editedItems];
      updatedItems[existingItemIndex].qty += selectedQuantity;
      setEditedItems(updatedItems);
    } else {
      // Add new item
      const newItem = {
        menuItemId: menuItem.id,
        name: menuItem.name,
        price: adjustedPrice,
        qty: selectedQuantity
      };
      setEditedItems([...editedItems, newItem]);
    }
    
    // Reset selection
    setSelectedMenuItem('');
    setSelectedQuantity(1);
    setSearchTerm('');
  };
  
  // Function to remove an item from the order
  const handleRemoveItem = (index) => {
    const updatedItems = [...editedItems];
    updatedItems.splice(index, 1);
    setEditedItems(updatedItems);
  };
  
  // Function to update item quantity
  const handleUpdateQuantity = (index, delta) => {
    const updatedItems = [...editedItems];
    const newQty = Math.max(1, updatedItems[index].qty + delta);
    updatedItems[index].qty = newQty;
    setEditedItems(updatedItems);
  };
  
  // Function to save the edited order
  const handleSaveOrder = async () => {
    if (!currentOrder || editedItems.length === 0) {
      setIsEditModalOpen(false);
      return;
    }
    
    setIsUpdating(true);
    try {
      // Calculate new total amount
      const totalAmount = editedItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
      
      const updates = {
          items: editedItems,
          totalAmount: totalAmount
      };

      // Update seatCategory name if it's a table order
      const orderType = currentOrder.phoneNumber && currentOrder.customerAddress ? 'online' : 'table';
      if (orderType === 'table' && selectedModalSeatAreaId) {
          const cat = (restaurant.seatCategories || []).find(c => c.id === selectedModalSeatAreaId);
          if (cat) updates.seatAre = cat.name; // Save as seatArea, maybe keeping seatCategory for redundancy isn't bad but let's stick to plan
          if (cat) updates.seatArea = cat.name;
      }

      // Update the order in Firestore
      await updateOrderDetails(restaurantId, currentOrder.id, updates);
      
      // Close modal and reset states
      setIsEditModalOpen(false);
      setCurrentOrder(null);
      setEditedItems([]);
      setSelectedMenuItem('');
      setSelectedQuantity(1);
      
      // Success message
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error updating order:', err);
      setError('Failed to update order. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Function to close the edit modal
  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setCurrentOrder(null);
    setEditedItems([]);
    setSelectedMenuItem('');
    setSelectedQuantity(1);
    setSearchTerm('');
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(restaurantId, orderId, newStatus);
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('Failed to update order status. Please try again.');
    }
  };

  const handleMoveToNextStep = async (orderId) => {
    try {
      await moveOrderToNextStep(restaurantId, orderId);
    } catch (err) {
      console.error('Error moving order to next step:', err);
      setError('Failed to move order to next step. Please try again.');
    }
  };

  const handleViewDetails = (order) => {
    handleEditOrder(order);
  };

  if (loading && Object.keys(orders).length === 0) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="container centered"><p className="error-message">{error}</p></div>;
  }

  const renderTabContent = () => {
    const currentOrders = orders[activeTab] || [];
    
    if (loading) {
      return <div className="loading">Loading orders...</div>;
    }

    if (currentOrders.length === 0) {
      const currentStep = workflowSteps.find(step => step.id === activeTab);
      return (
        <div className="no-orders">
          <p>No orders in {currentStep?.name || 'this tab'}</p>
        </div>
      );
    }

    return (
      <div className="orders-list">
        {currentOrders.map(order => (
          <div key={order.id} className="order-card">
            <h3>{order.phoneNumber && order.customerAddress ? 'Online Order' : 'Table Order'} #: {order.orderNumber || order.id}</h3>
            <p><strong>Customer:</strong> {order.customerName}</p>
            {order.phoneNumber && order.customerAddress ? (
              <>
                <p>
                  <strong>Phone:</strong> {order.phoneNumber}
                  {order.phoneNumber && (
                    <>
                      {' '}
                      <a
                        className="btn btn-primary"
                        href={`tel:${order.phoneNumber}`}
                        style={{ padding: '6px 10px', fontSize: '0.9rem', marginLeft: '6px', backgroundColor: '#2563eb', color: '#fff', border: 'none' }}
                        aria-label="Call customer"
                      >
                        <FaPhone style={{ verticalAlign: 'middle' }} />
                      </a>
                      {' '}
                      <a
                        className="btn"
                        href={`https://wa.me/${String(order.phoneNumber).replace(/\\D/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ padding: '6px 10px', fontSize: '0.9rem', marginLeft: '6px', backgroundColor: '#25D366', color: '#fff', border: 'none' }}
                        aria-label="Chat on WhatsApp"
                      >
                        <FaWhatsapp style={{ verticalAlign: 'middle' }} />
                      </a>
                    </>
                  )}
                </p>
                <p><strong>Address:</strong> {order.customerAddress}</p>
                {order.addressType && (<p><strong>Type:</strong> {order.addressType}</p>)}
                {(order.addressLine1 || order.addressLine2) && (
                  <p><strong>Address Lines:</strong> {[order.addressLine1, order.addressLine2].filter(Boolean).join(', ')}</p>
                )}
                {(order.area || order.city || order.pincode) && (
                  <p><strong>Area/City/Pincode:</strong> {[order.area, order.city, order.pincode].filter(Boolean).join(', ')}</p>
                )}
                {order.landmark && (<p><strong>Landmark:</strong> {order.landmark}</p>)}
                {order.deliveryInstructions && (<p><strong>Instructions:</strong> {order.deliveryInstructions}</p>)}
                {order.customerLatitude && order.customerLongitude && (
                  <p>
                    <strong>Location:</strong>{' '}
                    <a
                      className="btn btn-primary"
                      href={`https://www.google.com/maps?q=${order.customerLatitude},${order.customerLongitude}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ padding: '6px 10px', fontSize: '0.9rem', marginLeft: '6px', backgroundColor: '#2563eb', color: '#fff', border: 'none' }}
                    >
                      View on Map
                    </a>
                  </p>
                )}
              </>
            ) : (
              <p><strong>Table:</strong> {order.tableNumber}</p>
            )}
            <p><strong>Total:</strong> {currencySymbol}{order.totalAmount ? order.totalAmount.toFixed(2) : 'N/A'}</p>
            <p><strong>Items:</strong> {order.items.map(item => `${item.name} (x${item.qty})`).join(', ')}</p>
            <p><strong>Received:</strong> {order.createdAt && (order.createdAt.toDate ? order.createdAt.toDate().toLocaleString() : new Date(order.createdAt).toLocaleString())}</p>
            {order.cancellationReason && (
              <p><strong>Cancellation Reason:</strong> {order.cancellationReason}</p>
            )}
            {order.status === activeTab && order.createdAt && <OrderTimer createdAt={order.createdAt} />}
            <div className="order-actions">
              <button onClick={() => handleEditOrder(order)} className="btn btn-primary" style={{marginTop: '10px', marginLeft: '10px'}}>Edit Items</button>
              {activeTab !== 'cancelled' && activeTab !== 'completed' && (
                <>
                  <button onClick={() => handleMoveToNextStep(order.id)} className="btn btn-primary" style={{marginTop: '10px', marginLeft: '10px'}}>Move to Next Step</button>
                  <button onClick={() => handleCancelOrder(order.id)} className="btn btn-danger" style={{marginTop: '10px', marginLeft: '10px'}}>Cancel Order</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="container orders-page">
      {/* Edit Order Modal */}
      {isEditModalOpen && currentOrder && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Order #{currentOrder.orderNumber || currentOrder.id}</h2>
              <button onClick={handleCloseModal} className="close-btn">&times;</button>
            </div>
            
            <div className="modal-body">
              {/* Seat Category Selection for Table Orders */}
              {currentOrder && (!currentOrder.phoneNumber || !currentOrder.customerAddress) && restaurant?.seatCategories?.length > 1 && (
                  <>
                    <h3>Seating Area</h3>
                    <div className="add-item-form" style={{ marginBottom: '20px' }}>
                        <div className="form-group">
                            <label>Area:</label>
                            <select 
                                value={selectedModalSeatAreaId} 
                                onChange={(e) => setSelectedModalSeatAreaId(e.target.value)}
                            >
                                {restaurant.seatCategories.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.name} ({cat.type === 'percent' ? `${cat.value > 0 ? '+' : ''}${cat.value}%` : `${cat.value > 0 ? '+' : ''}${getCurrencySymbol(restaurant?.currency)}${cat.value}`})
                                    </option>
                                ))}
                            </select>
                            <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                                Changing this will update prices for all items in the order.
                            </small>
                        </div>
                    </div>
                  </>
              )}

              <h3>Current Items</h3>
              {editedItems.length === 0 ? (
                <p>No items in this order.</p>
              ) : (
                <div className="order-items-list">
                  {editedItems.map((item, index) => (
                    <div key={index} className="order-item">
                      <div className="item-details">
                        <span>{item.name}</span>
                        <span>{currencySymbol}{item.price.toFixed(2)}</span>
                      </div>
                      <div className="item-actions">
                        <button onClick={() => handleUpdateQuantity(index, -1)} disabled={item.qty <= 1}>-</button>
                        <span>{item.qty}</span>
                        <button onClick={() => handleUpdateQuantity(index, 1)}>+</button>
                        <button onClick={() => handleRemoveItem(index)} className="remove-btn">Remove</button>
                      </div>
                    </div>
                  ))}
                  <div className="order-total">
                    <strong>Total: {currencySymbol}{editedItems.reduce((sum, item) => sum + (item.price * item.qty), 0).toFixed(2)}</strong>
                  </div>
                </div>
              )}
              
              <h3>Add Items</h3>
              <div className="add-item-form">
                <div className="form-group">
                  <label>Menu Item:</label>
                  <input 
                    type="text" 
                    placeholder="Search for menu items..."
                    value={searchTerm || ''}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="search-results">
                    {searchTerm && availableMenuItems
                      .filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map(item => {
                        const orderType = currentOrder && currentOrder.phoneNumber && currentOrder.customerAddress ? 'online' : 'table';
                        const basePrice = item.offerPrice && item.offerPrice < item.price ? item.offerPrice : item.price;
                        
                        let adjustment = null;
                        if (orderType === 'table' && selectedModalSeatAreaId) {
                            adjustment = (restaurant.seatCategories || []).find(c => c.id === selectedModalSeatAreaId);
                        }

                        const displayPrice = applyPriceAdjustment(basePrice, restaurant, orderType, adjustment);
                        
                        return (
                        <div 
                          key={item.id} 
                          className="search-item" 
                          onClick={() => {
                            setSelectedMenuItem(item.id);
                            setSearchTerm(item.name);
                          }}
                        >
                          {item.name} - {currencySymbol}{displayPrice.toFixed(2)}
                        </div>
                      )})}
                  </div>
                </div>
                <div className="form-group">
                  <label>Quantity:</label>
                  <input 
                    type="number" 
                    min="1" 
                    value={selectedQuantity} 
                    onChange={(e) => setSelectedQuantity(parseInt(e.target.value) || 1)}
                  />
                </div>
                <button onClick={handleAddMenuItem} className="btn btn-primary">Add Item</button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseModal} className="btn">Cancel</button>
              <button 
                onClick={handleSaveOrder} 
                className="btn btn-success" 
                disabled={isUpdating || editedItems.length === 0}
              >
                {isUpdating ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Orders</h1>
        <Link to={`/dashboard/${restaurantId}`} className="btn" style={{width:"fit-content"}}>Back</Link>
      </div>

      <div className="tabs">
        {workflowSteps.map(step => {
          const stepOrders = orders[step.id] || [];
          return (
            <button 
              key={step.id}
              className={`tab-button ${activeTab === step.id ? 'active' : ''}`}
              onClick={() => setActiveTab(step.id)}
            >
              {step.name} ({stepOrders.length})
            </button>
          );
        })}
      </div>

      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
}

export default OrdersPage;
